import {
  AspectBounds,
  AspectDimensions,
  Coordinates,
  Dimensions,
  MinDimensions,
  Position,
} from "types/Positioning";

export function getPixelPosition(position: Position, screenSize: Dimensions): Coordinates {
  return {
    x: ((position.left || 0) / 100) * (screenSize.width || 0),
    y: ((position.top || 0) / 100) * (screenSize.height || 0),
  };
}

export function getPercentagePosition(position: Coordinates, screenSize: Dimensions): Position {
  if (!screenSize.width || !screenSize.height) return { left: 0, top: 0 };

  return {
    left: (position.x * 100) / screenSize.width,
    top: (position.y * 100) / screenSize.height,
  };
}

export function getPixelDimensions(
  aspectDimensions: AspectDimensions,
  screenSize: Dimensions,
): Dimensions {
  const width = ((aspectDimensions.widthPercentage || 0) / 100) * (screenSize.width || 0);
  return {
    width,
    height: width / aspectDimensions.aspectRatio,
  };
}

export function getSpotlightSize(screenSize: Dimensions, pixelDimensions: Dimensions): Dimensions {
  const aspectRatio = pixelDimensions.width! / pixelDimensions.height!;
  if (
    (pixelDimensions.width || 0) / (screenSize.width || 0) >
    (pixelDimensions.height || 0 / (screenSize.height || 0))
  ) {
    return { width: screenSize.width!, height: screenSize.width! / aspectRatio };
  } else {
    return { width: screenSize.height! * aspectRatio * 0.8, height: screenSize.height! * 0.8 };
  }
}

export function getAspectDimensions(
  pixelDimensions: Dimensions,
  screenSize: Dimensions,
): AspectDimensions {
  return {
    widthPercentage: ((pixelDimensions.width || 0) * 100) / (screenSize.width || 1),
    aspectRatio: (pixelDimensions.width || 0) / (pixelDimensions.height || 1),
  };
}

const clamp = (num: number, min: number, max: number): number => Math.min(Math.max(num, min), max);

export function clampDimensions(
  pixelDimensions: Dimensions,
  pixelCoords: Coordinates,
  minDimensions: MinDimensions,
  screenSize: Dimensions,
  aspectBounds: AspectBounds,
): Dimensions {
  if (!screenSize.width || !screenSize.height) return pixelDimensions;
  const size = {
    width: clamp(pixelDimensions.width || 0, minDimensions.minWidth || 0, screenSize.width),
    height: clamp(pixelDimensions.height || 0, minDimensions.minHeight || 0, screenSize.height),
  };

  return clampAspect(size, aspectBounds, screenSize, pixelCoords);
}

export function clampAspect(
  pixelDimensions: Dimensions,
  aspectBounds: AspectBounds,
  screenSize: Dimensions,
  pixelCoords: Coordinates,
) {
  if (!pixelDimensions.width || !pixelDimensions.height || !screenSize.width || !screenSize.height)
    return pixelDimensions;

  const aspect = pixelDimensions.width / pixelDimensions.height;
  if (aspectBounds.minAspectRatio) {
    if (aspect < aspectBounds.minAspectRatio) {
      const maxWidth = screenSize.width;
      pixelDimensions.width = Math.min(
        maxWidth,
        pixelDimensions.height * aspectBounds.minAspectRatio,
      );
      pixelDimensions.height = pixelDimensions.width / aspectBounds.minAspectRatio;
    }
  }

  if (aspectBounds.maxAspectRatio) {
    if (aspect > aspectBounds.maxAspectRatio) {
      const maxHeight = screenSize.height;
      pixelDimensions.height = Math.min(
        maxHeight,
        pixelDimensions.width / aspectBounds.maxAspectRatio,
      );
      pixelDimensions.width = pixelDimensions.height * aspectBounds.maxAspectRatio;
    }
  }
  return pixelDimensions;
}
